'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

/**
 * Loads the chat widget off the critical path.
 *
 * The widget (plus the in-thread booking mini-app it pulls in) was imported by
 * the root layout, so its JavaScript was part of the shared bundle every route
 * paid for before it could become interactive - on a throttled phone that is
 * main-thread time spent on something nobody has asked for yet.
 *
 * Nothing about the widget is needed for the first paint:
 *   - `defer="idle"` waits for the browser to go idle, or for the first sign of
 *     a real visitor (a pointer, a key, a scroll), whichever comes first.
 *   - `defer="in-view"` waits until the placeholder is actually scrolled near,
 *     which is right for the demo instance sitting far below the fold.
 */

const ChatWidget = dynamic(
  () => import('./chat-widget').then((m) => ({ default: m.ChatWidget })),
  { ssr: false },
);

const WAKE_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;

export function ChatWidgetLazy({
  variant = 'inline',
  defer = 'idle',
}: {
  variant?: 'inline' | 'floating';
  defer?: 'idle' | 'in-view';
}) {
  const [ready, setReady] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    const load = () => {
      if (!cancelled) setReady(true);
    };

    if (defer === 'in-view') {
      const el = holder.current;
      if (!el || typeof IntersectionObserver === 'undefined') {
        load();
        return;
      }
      // 400px of lead time, so it is ready by the time it is read.
      const io = new IntersectionObserver(
        (entries) => entries.some((e) => e.isIntersecting) && load(),
        { rootMargin: '400px' },
      );
      io.observe(el);
      return () => {
        cancelled = true;
        io.disconnect();
      };
    }

    const canIdle = typeof window.requestIdleCallback === 'function';
    const idle: number = canIdle
      ? window.requestIdleCallback(load, { timeout: 2500 })
      : window.setTimeout(load, 1500);

    for (const evt of WAKE_EVENTS) {
      window.addEventListener(evt, load, { once: true, passive: true });
    }

    return () => {
      cancelled = true;
      if (canIdle) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
      for (const evt of WAKE_EVENTS) window.removeEventListener(evt, load);
    };
  }, [defer, ready]);

  if (ready) return <ChatWidget variant={variant} />;

  // The floating bubble has nothing to reserve. The inline demo does: hold its
  // height so the section below does not jump when the widget arrives.
  return variant === 'floating' ? (
    <div ref={holder} aria-hidden="true" />
  ) : (
    <div
      ref={holder}
      className="glass bevel rounded-card"
      style={{ minHeight: '30rem' }}
      aria-hidden="true"
    />
  );
}
